/* Copyright (c) 2020-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { When } from "react-if";
import { ExtendedPaginationBox } from "@nordcloud/gnui";
import { useQueryState } from "~/hooks";

type Props = {
  count: number;
  small?: boolean;
  page?: number;
  limit?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
};

export function Pagination({
  count,
  small,
  page,
  limit,
  onPageChange,
  onLimitChange,
}: Props) {
  const { queryState, updateQueryState } = useQueryState();
  const isControlled = page !== undefined && limit !== undefined;
  const effectivePage = isControlled ? page : Number(queryState.page);
  const effectiveLimit = isControlled ? limit : Number(queryState.limit);

  const visible = useMemo(() => count > 0, [count]);

  return (
    <When condition={visible}>
      <ExtendedPaginationBox
        small={small}
        count={count}
        firstPage={1}
        from={startFrom(effectivePage, effectiveLimit)}
        setPage={(newPage) => {
          if (isControlled) {
            onPageChange?.(startByPage(newPage, effectiveLimit));
          } else {
            updateQueryState({
              ...queryState,
              page: String(startByPage(newPage, effectiveLimit)),
            });
          }
        }}
        setSize={(newLimit) => {
          if (isControlled) {
            onLimitChange?.(newLimit);
            onPageChange?.(1);
          } else {
            updateQueryState({
              limit: String(newLimit),
              page: "1",
            });
          }
        }}
        size={Number(effectiveLimit)}
      />
    </When>
  );
}

const startByPage = (currentPage: number, pageLimit: number) =>
  currentPage / pageLimit;

const startFrom = (currentPage: number, pageLimit: number) =>
  currentPage * pageLimit;
